import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 5;
const ACCEPT = ".pdf,.doc,.docx,.txt,.rtf";

export default function CareerApplyModal({ role, open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", linkedin: "", cover_note: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const inputCls = "w-full bg-white border border-[var(--dm-border)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-emerald)]";
  const labelCls = "block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--dm-muted)] mb-2";

  const submit = async (e) => {
    e.preventDefault();
    if (file && file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Resume must be under ${MAX_MB} MB.`);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.phone)      fd.append("phone", form.phone);
      if (form.linkedin)   fd.append("linkedin", form.linkedin);
      if (form.cover_note) fd.append("cover_note", form.cover_note);
      fd.append("role_slug", role.slug);
      fd.append("role_title", role.title);
      if (file) fd.append("resume", file);
      await axios.post(`${API}/careers/apply`, fd);
      toast.success("Application submitted — we'll get back within one business day.");
      setForm({ name: "", email: "", phone: "", linkedin: "", cover_note: "" });
      setFile(null);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return createPortal(
    <div
      data-testid="career-apply-modal"
      className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-[var(--dm-border)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--dm-border)] bg-[var(--dm-navy)] text-white">
          <div className="min-w-0">
            <div className="dm-overline text-white/70">Apply for role</div>
            <div className="font-serif text-xl truncate">{role.title}</div>
          </div>
          <button data-testid="career-apply-close" onClick={onClose} className="p-2 hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="p-6 grid gap-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Full name</label>
              <input required data-testid="ca-name" className={inputCls} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            </div>
            <div><label className={labelCls}>Email</label>
              <input required type="email" data-testid="ca-email" className={inputCls} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Phone</label>
              <input data-testid="ca-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
            </div>
            <div><label className={labelCls}>LinkedIn (optional)</label>
              <input data-testid="ca-linkedin" placeholder="https://linkedin.com/in/..." className={inputCls} value={form.linkedin} onChange={(e) => setForm({...form, linkedin: e.target.value})} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Resume (PDF, DOC, DOCX — max {MAX_MB} MB)</label>
            <label
              htmlFor="career-resume-input"
              className="flex items-center gap-3 border border-dashed border-[var(--dm-border)] px-4 py-4 cursor-pointer hover:border-[var(--dm-emerald)] transition-colors"
            >
              <Upload className="w-4 h-4 text-[var(--dm-emerald)]" />
              <span className="text-sm text-[var(--dm-navy)] truncate flex-1">
                {file ? file.name : "Click to select a resume file"}
              </span>
              {file && <Paperclip className="w-4 h-4 text-[var(--dm-muted)]" />}
              <input
                id="career-resume-input"
                data-testid="ca-resume"
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            {file && (
              <button type="button" onClick={() => setFile(null)} className="text-xs text-[var(--dm-muted)] hover:text-[var(--dm-navy)] mt-2">
                Remove file
              </button>
            )}
          </div>

          <div>
            <label className={labelCls}>Cover note (optional)</label>
            <textarea rows={4} data-testid="ca-cover" className={inputCls}
              placeholder="A few lines about why this role and what you'd bring."
              value={form.cover_note} onChange={(e) => setForm({...form, cover_note: e.target.value})} />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={loading} data-testid="ca-submit" className="dm-btn-primary">
              {loading ? "Submitting…" : "Submit application"}
            </button>
            <p className="text-xs text-[var(--dm-muted)]">Sent to contact@datameridianconsulting.com</p>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
